import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { CheckCircle2, XCircle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);
const AUTO_DISMISS_MS = 3200;

const TONE_META = {
  success: { icon: CheckCircle2, iconClass: 'text-emerald-600', barClass: 'bg-emerald-500' },
  error: { icon: XCircle, iconClass: 'text-red-600', barClass: 'bg-red-500' },
  info: { icon: Info, iconClass: 'text-emerald-700', barClass: 'bg-emerald-500' },
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((message, { tone = 'success' } = {}) => {
    const id = ++idRef.current;
    setToasts((current) => [...current, { id, message, tone }]);
    setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
  }, [dismiss]);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="fixed z-[100] bottom-20 sm:bottom-6 left-1/2 -translate-x-1/2 flex flex-col-reverse gap-2 w-[calc(100%-2rem)] max-w-sm px-0 pointer-events-none"
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map((toast) => {
          const meta = TONE_META[toast.tone] || TONE_META.success;
          const Icon = meta.icon;
          return (
            <div
              key={toast.id}
              role="status"
              className="pointer-events-auto relative flex items-center gap-3 bg-white rounded-2xl shadow-floating border border-emerald-100 pl-4 pr-3 py-3 overflow-hidden animate-fade-in"
            >
              <div className={`absolute left-0 top-0 bottom-0 w-1 ${meta.barClass}`} aria-hidden="true" />
              <Icon size={18} className={`shrink-0 ${meta.iconClass}`} aria-hidden="true" />
              <p className="flex-1 text-sm font-medium text-emerald-950">{toast.message}</p>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                aria-label="Dismiss notification"
                className="shrink-0 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700"
              >
                <X size={14} aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
};
